import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_CONFIG, corsProxiedUrl } from '../config/api';

const MILESTONE_STEP = 50;
const REFRESH_INTERVAL = 30_000;
const CELEBRATION_DURATION = 15_000;
const _params = new URLSearchParams(window.location.search);
const DEMO_MODE = _params.get('demo') === 'true';
const DEMO_UNITS = parseInt(_params.get('units') || '250', 10);

function isOnMilestone(units: number): boolean {
  return units > 0 && units % MILESTONE_STEP === 0;
  
}

// ── Amount in words (Indian system) ──────────────────────────────────────────
const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
  'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
function numWords(n: number): string {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + ONES[n % 10] : '');
  return ONES[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 ? ' ' + numWords(n % 100) : '');
}
function amountInWords(n: number): string {
  const amt = Math.floor(n);
  const parts: string[] = [];
  const crore = Math.floor(amt / 10_000_000);
  const lakh  = Math.floor((amt % 10_000_000) / 100_000);
  const thou  = Math.floor((amt % 100_000) / 1_000);
  const rem   = amt % 1_000;
  if (crore) parts.push(numWords(crore) + ' CRORE');
  if (lakh)  parts.push(numWords(lakh)  + ' LAKH');
  if (thou)  parts.push(numWords(thou)  + ' THOUSAND');
  if (rem)   parts.push(numWords(rem));
  return parts.join(' ') || 'ZERO';
}

// ── Confetti ──────────────────────────────────────────────────────────────────
interface Particle { id: number; x: number; color: string; delay: number; duration: number; size: number; }
function generateParticles(count = 160): Particle[] {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#FFA500', '#7CFC00'];
  return Array.from({ length: count }, (_, i) => ({
    id: i, x: Math.random() * 100, color: colors[i % colors.length],
    delay: Math.random() * 3, duration: 2 + Math.random() * 3, size: 6 + Math.random() * 10,
  }));
}

// ── Big number ────────────────────────────────────────────────────────────────
function AnimatedNumber({ value, color = '#FFFFFF' }: { value: number; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 2 }}>
      {String(value).split('').map((d, i) => (
        <span key={i} style={{
          display: 'inline-block',
          fontSize: 'clamp(90px, 18vw, 240px)',
          fontWeight: 900,
          fontFamily: '"Inter","Arial Black",sans-serif',
          color,
          lineHeight: 1,
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          textShadow: `0 0 80px ${color}44`,
          letterSpacing: '-6px',
        }}>{d}</span>
      ))}
    </div>
  );
}

// ── Amount pill ───────────────────────────────────────────────────────────────
function formatIndian(n: number): string {
  const s = Math.floor(Math.abs(n)).toString();
  let result = '';
  if (s.length <= 3) { result = s; }
  else {
    result = s.slice(-3);
    let rem = s.slice(0, -3);
    while (rem.length > 2) { result = rem.slice(-2) + ',' + result; rem = rem.slice(0, -2); }
    if (rem) result = rem + ',' + result;
  }
  return (n < 0 ? '-' : '') + result;
}

function AmountBadge({ amount, celebrating }: { amount: number; celebrating: boolean }) {
  const color = celebrating ? '#FFD700' : '#4ECDC4';
  return (
    <div style={{ marginTop: 36, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', letterSpacing: 5, textTransform: 'uppercase', fontFamily: '"Inter",sans-serif', marginBottom: 10 }}>
        Amount Collected
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{
          fontSize: 'clamp(44px, 7vw, 100px)', fontWeight: 900,
          fontFamily: '"Inter","Arial Black",sans-serif',
          background: `linear-gradient(135deg, ${color}, #fff8dc, ${color}cc)`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          filter: `drop-shadow(0 0 32px ${color}88)`,
          lineHeight: 1,
          animation: celebrating ? 'textPulse 1.5s ease-in-out infinite' : 'none',
        }}>
          ₹{formatIndian(amount)}
        </span>
      </div>
      <div style={{
        marginTop: 10,
        color: color,
        fontSize: 'clamp(16px, 2vw, 28px)',
        letterSpacing: 4,
        fontFamily: '"Inter",sans-serif',
        fontWeight: 700,
        textTransform: 'uppercase',
        opacity: 0.85,
        textShadow: `0 0 20px ${color}66`,
      }}>
        {amountInWords(amount)}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const UnitsCelebration: React.FC = () => {
  const [currentUnits, setCurrentUnits] = useState<number | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [celebrating, setCelebrating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [particles] = useState<Particle[]>(generateParticles);
  const [showAmount, setShowAmount] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const celebTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUnits = async () => {
    if (DEMO_MODE) {
      setCurrentUnits(DEMO_UNITS);
      setTotalAmount(DEMO_UNITS * 350_000);
      setLastUpdated(new Date());
      setCelebrating(true);
      setLoading(false);
      return;
    }
    try {
      const akBase = corsProxiedUrl('https://animalkart-live-apis-jn6cma3vvq-el.a.run.app');
      const akToken = API_CONFIG.getAnimalKartToken();
      const resp = await axios.get(`${akBase}/purchases/admin/units/pending`, {
        headers: { Authorization: `Bearer ${akToken}`, 'x-admin-mobile': '8977920290' },
      });
      const units = resp.data?.total_units_approved_count ?? 0;
      const amount = resp.data?.total_amount_collected ?? 0;
      setCurrentUnits(units);
      setTotalAmount(amount);
      setLastUpdated(new Date());
      setError('');
      if (isOnMilestone(units)) {
        setCelebrating(true);
        if (celebTimerRef.current) clearTimeout(celebTimerRef.current);
        celebTimerRef.current = setTimeout(() => setCelebrating(false), CELEBRATION_DURATION);
      }
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
    intervalRef.current = setInterval(fetchUnits, REFRESH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (celebTimerRef.current) clearTimeout(celebTimerRef.current);
    };
  }, []);


  return (
    <div style={{
      minHeight: '100vh', width: '100vw',
      background: celebrating
        ? 'radial-gradient(ellipse at 50% 40%, #1a0a2e 0%, #060608 100%)'
        : 'radial-gradient(ellipse at 50% 30%, #071428 0%, #060608 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
      padding: '20px', boxSizing: 'border-box',
      fontFamily: '"Inter", sans-serif',
    }}>

      {/* Subtle grid overlay */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {/* Confetti */}
      {celebrating && particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', left: `${p.x}%`, top: '-20px',
          width: `${p.size}px`, height: `${p.size}px`,
          background: p.color,
          borderRadius: p.id % 3 === 0 ? '50%' : p.id % 3 === 1 ? '2px' : '0',
          animation: `confettiFall ${p.duration}s ${p.delay}s ease-in infinite`,
          opacity: 0, zIndex: 1,
        }} />
      ))}

      {/* Glow rings */}
      {celebrating && (
        <>
          <div style={{ position: 'absolute', width: '55vw', height: '55vw', borderRadius: '50%', border: '1px solid rgba(255,215,0,0.12)', animation: 'ringPulse 2s ease-in-out infinite', zIndex: 1 }} />
          <div style={{ position: 'absolute', width: '80vw', height: '80vw', borderRadius: '50%', border: '1px solid rgba(255,215,0,0.05)', animation: 'ringPulse 2s 0.7s ease-in-out infinite', zIndex: 1 }} />
        </>
      )}

      {/* Last updated */}
      <div style={{ position: 'absolute', top: 16, right: 36, color: 'rgba(255,255,255,0.2)', fontSize: 12, fontFamily: 'monospace', zIndex: 10 }}>
        {lastUpdated ? `↻ ${lastUpdated.toLocaleTimeString()}` : ''}
      </div>

      {/* Settings button */}
      <div style={{ position: 'absolute', top: 12, left: 16, zIndex: 20 }}>
        <button
          onClick={() => setSettingsOpen(o => !o)}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: 16,
            transition: 'background 0.2s',
          }}
          title="Settings"
        >
          ⚙
        </button>

        {/* Settings panel */}
        {settingsOpen && (
          <div style={{
            marginTop: 8,
            background: 'rgba(10,20,40,0.92)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '14px 18px',
            minWidth: 200,
            backdropFilter: 'blur(12px)',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
              Display Settings
            </div>
            {/* Show Amount toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <div
                onClick={() => setShowAmount(v => !v)}
                style={{
                  width: 44, height: 24, borderRadius: 12,
                  background: showAmount ? '#4ECDC4' : 'rgba(255,255,255,0.15)',
                  position: 'relative', transition: 'background 0.25s', flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: 3, left: showAmount ? 23 : 3,
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.25s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                }} />
              </div>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontFamily: '"Inter",sans-serif' }}>
                Show Amount
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', width: '100%' }}>
        {/* Logo — inline above units count */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', marginBottom: 8, marginTop: 60 }}>
          <span style={{ fontSize: 130, fontWeight: 900, fontFamily: '"Inter","Arial Black",sans-serif', lineHeight: 1, color: '#FF8C00' }}>Farm</span>
          <span style={{ fontSize: 130, fontWeight: 900, fontFamily: '"Inter","Arial Black",sans-serif', lineHeight: 1, color: '#4CAF50' }}>Vest</span>
        </div>

        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 28, letterSpacing: 4 }}>LOADING...</div>
        ) : error ? (
          <div style={{ color: '#FF6B6B', fontSize: 22 }}>{error}</div>
        ) : celebrating ? (
          // ── CELEBRATION ─────────────────────────────────────────────────────
          <>
            <div style={{
              fontSize: 'clamp(16px, 2.5vw, 36px)', color: '#FFD700', fontWeight: 800,
              letterSpacing: '8px', textTransform: 'uppercase',
              animation: 'textPulse 1.5s ease-in-out infinite', marginBottom: 16,
            }}>
              🎉 MILESTONE ACHIEVED 🎉
            </div>

            <AnimatedNumber value={currentUnits!} color="#FFD700" />

            <div style={{
              fontSize: 'clamp(14px, 2vw, 26px)', color: 'rgba(255,215,0,0.7)',
              fontWeight: 600, letterSpacing: '10px', textTransform: 'uppercase', marginTop: 8,
            }}>
              UNITS
            </div>

            {showAmount && totalAmount > 0 && <AmountBadge amount={totalAmount} celebrating={true} />}

            <div style={{ marginTop: 36, display: 'flex', gap: 20, justifyContent: 'center' }}>
              {['🏆', '🌟', '🎊', '💪', '🚀'].map((e, i) => (
                <span key={i} style={{ fontSize: 'clamp(32px, 5vw, 56px)', animation: `bounce 1s ${i * 0.15}s ease-in-out infinite`, display: 'inline-block' }}>{e}</span>
              ))}
            </div>

          </>
        ) : (
          // ── COUNTDOWN ───────────────────────────────────────────────────────
          <>
            <AnimatedNumber value={currentUnits!} />

            <div style={{
              fontSize: 'clamp(12px, 1.6vw, 20px)', color: 'rgba(255,255,255,0.35)',
              fontWeight: 500, letterSpacing: '8px', textTransform: 'uppercase', marginTop: 6,
            }}>
              UNITS
            </div>

            {showAmount && totalAmount > 0 && <AmountBadge amount={totalAmount} celebrating={false} />}


          </>
        )}
      </div>

      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes ringPulse {
          0%,100% { transform: scale(1); opacity: 0.5; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
        @keyframes textPulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.8; transform: scale(1.025); }
        }
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-16px); }
        }
      `}</style>
    </div>
  );
};

export default UnitsCelebration;
